import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';

import { AppConfigTopNavComponent } from '../../../shared/app-config-nav/app-config-top-nav.component';
import { RoundingRulesService } from './rounding-rules.service';
import {
  DepartmentListItem,
  PayGroupListItem,
  RoundingRule,
} from '../models/rounding-rules.model';

@Component({
  selector: 'app-rounding-rules',
  standalone: true,
  imports: [CommonModule, FormsModule, AppConfigTopNavComponent],
  templateUrl: './rounding-rules.component.html',
  styleUrls: ['./rounding-rules.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class RoundingRulesComponent implements OnInit, OnDestroy {
  roundingRules: RoundingRule[] = [];
  pagedRoundingRules: RoundingRule[] = [];

  payGroups: PayGroupListItem[] = [];
  departments: DepartmentListItem[] = [];

  isLoading = false;
  isSaving = false;
  errorMessage: string | null = null;

  currentPage = 1;
  totalPages = 1;
  pageSize = 25;
  pageSizes = [10, 25, 50, 100];

  modalOpen = false;
  isEditMode = false;
  currentRule: RoundingRule | null = null;
  formModel = this.getEmptyRuleForm();

  deleteConfirmOpen = false;
  ruleToDelete: RoundingRule | null = null;

  defaultConfirmOpen = false;
  ruleToMakeDefault: RoundingRule | null = null;

  roundingIntervalMinutes = 15;
  breakTimeHours = 0;
  breakTimeMinutes = 30;

  toastVisible = false;
  toastMessage = '';
  toastType: 'success' | 'error' = 'success';

  constructor(
    private roundingRulesService: RoundingRulesService,
    private cd: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.loadPageData();
  }

  ngOnDestroy(): void {
    this.unlockBodyScroll();
  }

  private lockBodyScroll(): void {
    document.body.style.overflow = 'hidden';
  }

  private unlockBodyScroll(): void {
    document.body.style.overflow = '';
  }

  private syncBodyScrollLock(): void {
    const hasOpenModal =
      this.modalOpen ||
      this.deleteConfirmOpen ||
      this.defaultConfirmOpen;

    if (hasOpenModal) {
      this.lockBodyScroll();
      return;
    }

    this.unlockBodyScroll();
  }

  loadPageData(): void {
    this.isLoading = true;
    this.errorMessage = null;

    Promise.all([
      this.roundingRulesService.getRoundingRules().toPromise(),
      this.roundingRulesService.getAllPayGroups().toPromise(),
      this.roundingRulesService.getAllDepartments().toPromise(),
    ])
      .then(([rules, payGroups, departments]) => {
        this.roundingRules = rules ?? [];
        this.payGroups = payGroups ?? [];
        this.departments = departments ?? [];

        this.sortRules();
        this.rebuildPagedRules();
      })
      .catch((err) => {
        console.error('Failed to load rounding rules page:', err);
        this.errorMessage = 'Failed to load rounding rules';
        this.roundingRules = [];
        this.pagedRoundingRules = [];
      })
      .finally(() => {
        this.isLoading = false;
        this.cd.detectChanges();
      });
  }

  private sortRules(): void {
    this.roundingRules = [...this.roundingRules].sort((a, b) => {
      if (a.isDefault && !b.isDefault) return -1;
      if (!a.isDefault && b.isDefault) return 1;
      return a.id - b.id;
    });
  }

  private rebuildPagedRules(): void {
    this.totalPages = Math.max(1, Math.ceil(this.roundingRules.length / this.pageSize));

    if (this.currentPage > this.totalPages) {
      this.currentPage = this.totalPages;
    }

    const start = (this.currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;

    this.pagedRoundingRules = this.roundingRules.slice(start, end);
  }

  changePage(page: number): void {
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page;
    this.rebuildPagedRules();
  }

  changePageSize(size: number): void {
    this.pageSize = Number(size) || 25;
    this.currentPage = 1;
    this.rebuildPagedRules();
  }

  openCreateModal(): void {
    this.isEditMode = false;
    this.currentRule = null;
    this.formModel = this.getEmptyRuleForm();

    this.roundingIntervalMinutes = 15;
    this.breakTimeHours = 0;
    this.breakTimeMinutes = 30;

    this.modalOpen = true;
    this.syncBodyScrollLock();
  }

  openEditModal(rule: RoundingRule): void {
    this.isEditMode = true;
    this.currentRule = rule;

    this.formModel = {
      payGroupId: rule.payGroup?.id ?? null,
      departmentId: rule.department?.id ?? null,
      roundingInterval: this.toTimeInputValue(rule.roundingInterval),
      breakTime: this.toTimeInputValue(rule.breakTime),
      breakThresholdHours: rule.breakThresholdHours ?? 0,
    };

    this.roundingIntervalMinutes = this.parseDurationToTotalMinutes(rule.roundingInterval);
    const breakTimeParts = this.parseDurationToHourMinute(rule.breakTime);
    this.breakTimeHours = breakTimeParts.hours;
    this.breakTimeMinutes = breakTimeParts.minutes;

    this.modalOpen = true;
    this.syncBodyScrollLock();
  }

  closeModal(force = false): void {
    if (this.isSaving && !force) return;

    this.modalOpen = false;
    this.isEditMode = false;
    this.currentRule = null;
    this.formModel = this.getEmptyRuleForm();

    this.roundingIntervalMinutes = 15;
    this.breakTimeHours = 0;
    this.breakTimeMinutes = 30;

    this.syncBodyScrollLock();
  }

  submitRule(form?: NgForm): void {
    const payGroupId = Number(this.formModel.payGroupId);
    const departmentId =
      this.formModel.departmentId === null || this.formModel.departmentId === undefined
        ? null
        : Number(this.formModel.departmentId);

    const roundingIntervalMinutes = Math.floor(Number(this.roundingIntervalMinutes));
    const breakTimeHours = Math.floor(Number(this.breakTimeHours));
    const breakTimeMinutes = Math.floor(Number(this.breakTimeMinutes));
    const breakThresholdHours = Number(this.formModel.breakThresholdHours);

    if (!Number.isFinite(payGroupId) || payGroupId <= 0) {
      this.showToast('Pay group is required', 'error');
      return;
    }

    if (!Number.isFinite(roundingIntervalMinutes) || roundingIntervalMinutes < 0) {
      this.showToast('Rounding interval minutes must be 0 or greater', 'error');
      return;
    }

    if (!Number.isFinite(breakTimeHours) || breakTimeHours < 0) {
      this.showToast('Break time hours must be 0 or greater', 'error');
      return;
    }

    if (!Number.isFinite(breakTimeMinutes) || breakTimeMinutes < 0 || breakTimeMinutes > 59) {
      this.showToast('Break time minutes must be between 0 and 59', 'error');
      return;
    }

    if (!Number.isFinite(breakThresholdHours) || breakThresholdHours < 0) {
      this.showToast('Break threshold hours must be 0 or greater', 'error');
      return;
    }

    this.formModel.roundingInterval = this.toApiTimeValueFromParts(0, roundingIntervalMinutes);
    this.formModel.breakTime = this.toApiTimeValueFromParts(breakTimeHours, breakTimeMinutes);

    const payload = {
      payGroupId,
      departmentId,
      roundingInterval: this.formModel.roundingInterval,
      breakTime: this.formModel.breakTime,
      breakThresholdHours,
    };

    this.isSaving = true;
    this.cd.detectChanges();

    const request$ =
      this.isEditMode && this.currentRule
        ? this.roundingRulesService.updateRoundingRule(this.currentRule.id, payload)
        : this.roundingRulesService.createRoundingRule(payload);

    request$.subscribe({
      next: (ok) => {
        if (!ok) {
          this.showToast(
            this.isEditMode ? 'Failed to update rounding rule' : 'Failed to create rounding rule',
            'error',
          );
          this.isSaving = false;
          this.cd.detectChanges();
          return;
        }

        this.isSaving = false;
        this.closeModal(true);
        this.showToast(
          this.isEditMode
            ? 'Rounding rule updated successfully'
            : 'Rounding rule created successfully',
          'success',
        );
        this.loadPageData();
      },
      error: (err) => {
        console.error(err);
        this.showToast(
          this.isEditMode ? 'Failed to update rounding rule' : 'Failed to create rounding rule',
          'error',
        );
        this.isSaving = false;
        this.cd.detectChanges();
      },
    });
  }

  confirmMakeDefault(rule: RoundingRule): void {
    if (rule.isDefault) return;

    this.ruleToMakeDefault = rule;
    this.defaultConfirmOpen = true;
    this.syncBodyScrollLock();
  }

  cancelMakeDefault(force = false): void {
    if (this.isSaving && !force) return;

    this.defaultConfirmOpen = false;
    this.ruleToMakeDefault = null;

    this.syncBodyScrollLock();
  }

  makeDefaultRule(): void {
    if (!this.ruleToMakeDefault) return;

    this.isSaving = true;
    this.cd.detectChanges();

    this.roundingRulesService.makeDefaultRoundingRule(this.ruleToMakeDefault.id).subscribe({
      next: (ok) => {
        if (!ok) {
          this.showToast('Failed to make rounding rule default', 'error');
          this.isSaving = false;
          this.cd.detectChanges();
          return;
        }

        this.isSaving = false;
        this.cancelMakeDefault(true);
        this.showToast('Default rounding rule updated successfully', 'success');
        this.loadPageData();
      },
      error: (err) => {
        console.error(err);
        this.showToast('Failed to make rounding rule default', 'error');
        this.isSaving = false;
        this.cd.detectChanges();
      },
    });
  }

  confirmDelete(rule: RoundingRule): void {
    this.ruleToDelete = rule;
    this.deleteConfirmOpen = true;
    this.syncBodyScrollLock();
  }

  cancelDelete(force = false): void {
    if (this.isSaving && !force) return;

    this.deleteConfirmOpen = false;
    this.ruleToDelete = null;

    this.syncBodyScrollLock();
  }

  deleteRule(): void {
    if (!this.ruleToDelete) return;

    this.isSaving = true;
    this.cd.detectChanges();

    this.roundingRulesService.deleteRoundingRule(this.ruleToDelete.id).subscribe({
      next: (ok) => {
        if (!ok) {
          this.showToast('Failed to delete rounding rule', 'error');
          this.isSaving = false;
          this.cd.detectChanges();
          return;
        }

        this.isSaving = false;
        this.cancelDelete(true);
        this.showToast('Rounding rule deleted successfully', 'success');
        this.loadPageData();
      },
      error: (err) => {
        console.error(err);
        this.showToast('Failed to delete rounding rule', 'error');
        this.isSaving = false;
        this.cd.detectChanges();
      },
    });
  }

  getDepartmentLabel(rule: RoundingRule): string {
    if (!rule.department) return 'All Departments';
    return `${rule.department.code} - ${rule.department.name}`;
  }

  getUpdatedByLabel(rule: RoundingRule): string {
    const user = rule.updatedBy ?? rule.createdBy;
    if (!user) return '—';

    const fullName = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
    return fullName || user.employeeNumber || '—';
  }

  formatDuration(value: string | null | undefined): string {
    if (!value) return '—';

    const parts = value.split(':');
    if (parts.length < 2) return value;

    const hours = Number(parts[0] ?? 0);
    const minutes = Number(parts[1] ?? 0);

    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
      return value;
    }

    if (hours > 0 && minutes > 0) {
      return `${hours}h ${minutes}m`;
    }

    if (hours > 0) {
      return `${hours}h`;
    }

    return `${minutes}m`;
  }

  formatThresholdHours(value: number | null | undefined): string {
    const n = Number(value ?? 0);
    return Number.isFinite(n) ? `${n} hrs` : '—';
  }

  trackByRuleId(_: number, item: RoundingRule): number {
    return item.id;
  }

  showToast(message: string, type: 'success' | 'error'): void {
    this.toastMessage = message;
    this.toastType = type;
    this.toastVisible = true;

    setTimeout(() => {
      this.toastVisible = false;
      this.cd.markForCheck();
    }, 2500);
  }

  private parseDurationToTotalMinutes(value: string | null | undefined): number {
    if (!value) return 0;

    const parts = value.split(':');
    const hours = Number(parts[0] ?? 0);
    const minutes = Number(parts[1] ?? 0);

    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
      return 0;
    }

    return hours * 60 + minutes;
  }

  private parseDurationToHourMinute(
    value: string | null | undefined,
  ): { hours: number; minutes: number } {
    if (!value) {
      return { hours: 0, minutes: 0 };
    }

    const parts = value.split(':');
    const hours = Number(parts[0] ?? 0);
    const minutes = Number(parts[1] ?? 0);

    return {
      hours: Number.isFinite(hours) ? hours : 0,
      minutes: Number.isFinite(minutes) ? minutes : 0,
    };
  }

  private toApiTimeValueFromParts(hours: number, minutes: number): string {
    const totalMinutes = Math.max(0, (Number(hours) || 0) * 60 + (Number(minutes) || 0));
    const normalizedHours = Math.floor(totalMinutes / 60);
    const normalizedMinutes = totalMinutes % 60;

    return `${String(normalizedHours).padStart(2, '0')}:${String(normalizedMinutes).padStart(2, '0')}:00`;
  }

  private toTimeInputValue(value: string | null | undefined): string {
    if (!value) return '00:15';
    const parts = value.split(':');
    if (parts.length >= 2) {
      return `${parts[0]}:${parts[1]}`;
    }
    return value;
  }

  private getEmptyRuleForm(): {
    payGroupId: number | null;
    departmentId: number | null;
    roundingInterval: string;
    breakTime: string;
    breakThresholdHours: number;
  } {
    return {
      payGroupId: null,
      departmentId: null,
      roundingInterval: '00:15',
      breakTime: '00:30',
      breakThresholdHours: 6.5,
    };
  }

  getDepartmentCodeLabel(rule: RoundingRule): string {
    return rule.department?.code || 'All';
  }
}